// Minimal in-house i18n: a plain nested dictionary plus a normalizer. There
// are only two languages, so this avoids pulling in a full i18n library.
//
// The dictionary below (auth/onboarding/notifications) is the
// original hand-written set. Newer sections live in ./i18n-sections/*.js —
// one file per feature area, each exporting `{ en: {...}, ar: {...} }` — and
// get merged in at the bottom of this file. Splitting it up this way keeps
// any one file from growing unmanageably large and lets unrelated areas be
// worked on without touching a single shared file.

import { shell } from "./i18n-sections/shell";
import { home } from "./i18n-sections/home";
import { dialogs } from "./i18n-sections/dialogs";
import { captionSettings } from "./i18n-sections/captionSettings";
import { editor } from "./i18n-sections/editor";

export const SUPPORTED_LANGS = ["en", "ar"];
export const DEFAULT_LANG = "ar";

export function normalizeLang(lang) {
  return SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
}

// For dynamic content (transcript text, user-typed names) rather than the
// static UI strings below — the `lang` attribute this feeds is what makes
// `:lang(ar)` in app.css switch to IBM Plex Sans Arabic; without it, Arabic
// text silently falls back to a system font since IBM Plex Sans itself has
// no Arabic glyphs.
const ARABIC_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
export function detectTextLang(text) {
  return text && ARABIC_RE.test(text) ? "ar" : undefined;
}

const RTL_LANGS = new Set(["ar"]);

// Whether a language code should read right-to-left. A transcript in Arabic
// still needs this set on the *whole* body even where a sentence code-switches
// into English mid-line (a brand name, a technical term) — that's the normal
// case for Iraqi Arabic speech, not the exception. Without an explicit `dir`,
// the browser's bidi algorithm falls back to the page's default `ltr` base
// direction, which is what makes a mixed line reorder oddly and puts
// trailing Arabic punctuation (a sentence-ending period) on the wrong visual
// side — `lang` alone (used for font selection via `:lang(ar)`) doesn't fix
// that, only `dir` does. Setting dir on the whole line, rather than per word,
// is what lets an embedded English run still sit correctly inside the
// Arabic-direction sentence instead of each word fending for itself.
export function isRtlLanguage(code) {
  return RTL_LANGS.has(code);
}

const baseTranslations = {
  en: {
    common: {
      brandName: "Arkii",
      continueWithGoogle: "Continue with Google",
      emailLabel: "Email",
      passwordLabel: "Password",
    },
    login: {
      title: "Welcome back",
      submit: "Log in",
      submitting: "Logging in...",
      noAccount: "Don't have an account?",
      signUpLink: "Sign up",
      verifiedNotice: "Email verified. You can now log in.",
    },
    signup: {
      title: "Create your account",
      submit: "Sign up",
      submitting: "Submitting...",
      hasAccount: "Already have an account?",
      logInLink: "Log in",
    },
    verifyEmail: {
      title: "Verify your email",
      subtitle: (email) => `Enter the 6-digit code we sent to ${email}.`,
      unverifiedNotice: "Please verify your email before logging in. We've sent a new code to your inbox.",
      codeLabel: "Verification code",
      expiresIn: (mmss) => `Expires in ${mmss}`,
      codeExpired: "This code has expired. Request a new one below.",
      submit: "Verify",
      submitting: "Verifying...",
      invalidCode: "Invalid or expired code.",
      tooManyAttempts: "Too many attempts — check the code or request a new one.",
      resendPrompt: "Didn't get a code?",
      resend: "Resend code",
      resending: "Sending...",
      resendCooldown: (s) => `Resend code (${s}s)`,
      resendSuccess: "A new code has been sent to your email.",
      resendRateLimited: "Too many requests. Please wait a moment and try again.",
      useDifferentEmail: "Use a different email",
    },
    passwordRules: {
      length: "At least 8 characters",
      uppercase: "At least one uppercase letter",
      lowercase: "At least one lowercase letter",
      number: "At least one number",
      symbol: "At least one symbol",
    },
    onboarding: {
      step1: {
        title: "Let's get you set up",
        nameLabel: "What is your name",
        namePlaceholder: "you can call me..",
        preferLabel: "I prefer...",
        next: "Next",
      },
      step2: {
        title: "Which one describes you the best?",
        personas: {
          personal_use: "Personal use",
          creator: "Creator",
          content_business: "Content business",
          voice_actor: "Voice actor",
          engineer: "Engineer",
          marketer: "Marketer",
          education: "Education",
          other: "Other",
        },
        back: "Back",
        skip: "Skip",
      },
      claimCredit: {
        titlePrefix: "Claim Your ",
        titleSuffix: " Credit",
        grantPrefix: "Because you are a part of ",
        grantAfterCompany: ", you are being granted ",
        creditAmount: (creditAmount) => `${creditAmount} minutes`,
        grantSuffix: " in credit.",
        claim: "Claim credit",
        claiming: "Claiming...",
        claimedTitle: "Credit claimed",
        claimedBody: (creditAmount) => `${creditAmount} have been added to your account.`,
      },
      startProject: {
        title: "Start your first project",
        subtitle: "Upload a file, record audio, or generate captions and we'll get it started for you.",
        skip: "Skip",
      },
    },
    transcribe: {
      title: "Transcription",
      subtitle: "Upload audio and get an accurate, timestamped transcript.",
      dropzoneCta: "Drag & drop audio, or click to browse",
      dropzoneHint: "MP3, WAV, M4A and more — up to 25MB",
      changeFile: "Change file",
      languageLabel: "Language",
      languageAr: "Arabic",
      languageEn: "English",
      submit: "Transcribe",
      submitting: "Uploading...",
      noFile: "Choose an audio file first.",
      fileTooLarge: "File is larger than 25MB.",
      resultTitle: "Result",
      detectedLanguage: "Detected language",
      duration: "Duration",
      billed: "Billed",
      copy: "Copy text",
      copied: "Copied",
      segments: "Segments",
      failedPrefix: "Failed",
      unknownError: "unknown error",
      balanceLabel: "Credit balance",
      minutesUnit: "m",
      secondsUnit: "s",
      historyTitle: "History",
      historyEmpty: "No transcripts yet.",
      noText: "(no text)",
      status: {
        pending: "Pending",
        processing: "Processing",
        completed: "Completed",
        failed: "Failed",
      },
    },
    notifications: {
      title: "Notifications",
      markAllRead: "Mark all read",
      markAsRead: "Mark as read",
      markAsUnread: "Mark as unread",
      allCaughtUp: "You’re all caught up",
      emptyDescription: "New notifications will appear here.",
      genericUpdate: "Notification update",
      untitledTranscript: "Untitled transcript",
      refunded: "The job failed and the deducted minutes were returned.",
      unavailable: "This item is no longer available.",
      loadError: "Couldn’t load notifications.",
      updateError: "Couldn’t update the notification.",
      retry: "Retry",
      loadMore: "Load more",
      loading: "Loading notifications",
      unreadCount: (count) => `${count} unread notification${count === 1 ? "" : "s"}`,
      download: "Download",
      downloading: "Downloading…",
      downloadError: "Couldn’t download the video.",
      copy: {
        transcription: {
          completed: "Your transcription is ready",
          failed: "Your transcription failed",
        },
        translation: {
          completed: "Your translation is ready",
          failed: "Your translation failed",
        },
        review: {
          completed: "Your AI review is ready",
          failed: "Your AI review failed",
        },
        video_export: {
          pending: "Your video export is rendering…",
          completed: "Your video export is ready",
          failed: "Your video export failed",
        },
      },
    },
  },
  ar: {
    common: {
      brandName: "آركـي",
      continueWithGoogle: "المتابعة باستخدام Google",
      emailLabel: "البريد الإلكتروني",
      passwordLabel: "كلمة المرور",
    },
    login: {
      title: "تسجيل الدخول",
      submit: "تسجيل الدخول",
      submitting: "جارٍ تسجيل الدخول...",
      noAccount: "ليس لديك حساب؟",
      signUpLink: "إنشاء حساب",
      verifiedNotice: "تم تأكيد بريدك الإلكتروني. يمكنك الآن تسجيل الدخول.",
    },
    signup: {
      title: "أنشئ حسابك",
      submit: "إنشاء حساب",
      submitting: "جارٍ الإرسال...",
      hasAccount: "لديك حساب بالفعل؟",
      logInLink: "تسجيل الدخول",
    },
    verifyEmail: {
      title: "تحقق من بريدك الإلكتروني",
      subtitle: (email) => `أدخل الرمز المكوّن من 6 أرقام الذي أرسلناه إلى ${email}.`,
      unverifiedNotice: "يرجى تأكيد بريدك الإلكتروني قبل تسجيل الدخول. أرسلنا رمزًا جديدًا إلى بريدك.",
      codeLabel: "رمز التحقق",
      expiresIn: (mmss) => `تنتهي الصلاحية خلال ${mmss}`,
      codeExpired: "انتهت صلاحية هذا الرمز. اطلب رمزًا جديدًا أدناه.",
      submit: "تحقّق",
      submitting: "جارٍ التحقق...",
      invalidCode: "الرمز غير صحيح أو منتهي الصلاحية.",
      tooManyAttempts: "محاولات كثيرة جدًا — تحقق من الرمز أو اطلب رمزًا جديدًا.",
      resendPrompt: "لم تصلك رسالة؟",
      resend: "إعادة إرسال الرمز",
      resending: "جارٍ الإرسال...",
      resendCooldown: (s) => `إعادة الإرسال (${s})`,
      resendSuccess: "تم إرسال رمز جديد إلى بريدك الإلكتروني.",
      resendRateLimited: "طلبات كثيرة جدًا. الرجاء الانتظار قليلاً ثم المحاولة مجددًا.",
      useDifferentEmail: "استخدام بريد إلكتروني آخر",
    },
    passwordRules: {
      length: "8 أحرف على الأقل",
      uppercase: "حرف كبير واحد على الأقل",
      lowercase: "حرف صغير واحد على الأقل",
      number: "رقم واحد على الأقل",
      symbol: "رمز واحد على الأقل",
    },
    onboarding: {
      step1: {
        title: "خلي نحضر حسابك",
        nameLabel: "الإسم الكريم",
        namePlaceholder: "إسمي ...",
        preferLabel: "أُفضّل...",
        next: "التالي",
      },
      step2: {
        title: "أفضل شي  يوصفني هو..",
        personas: {
          personal_use: "استخدام شخصي",
          creator: "صانع محتوى",
          content_business: "أعمال المحتوى",
          voice_actor: "ممثل صوتي",
          engineer: "مهندس",
          marketer: "مسوّق",
          education: "تعليم",
          other: "أخرى",
        },
        back: "رجوع",
        skip: "تخطي",
      },
      claimCredit: {
        titlePrefix: "يا هلا بـ",
        titleSuffix: "!",
        grantPrefix: "لأنك جزء من ",
        grantAfterCompany: "، سيتم منحك ",
        creditAmount: (creditAmount) => `${creditAmount} دقيقة`,
        grantSuffix: " من الرصيد.",
        claim: "استلم الرصيد",
        claiming: "جارٍ الاستلام...",
        claimedTitle: "تم استلام الرصيد",
        claimedBody: (creditAmount) => `تمت إضافة ${creditAmount} إلى حسابك.`,
      },
      startProject: {
        title: "خلي نبدي مشروعنا الأول!",
        subtitle: "ارفع ملف، سجّل صوتك، أو ولّد كابشن لڤيديو.",
        skip: "تخطي",
      },
    },
    transcribe: {
      title: "التفريغ الصوتي",
      subtitle: "ارفع ملفًا صوتيًا واحصل على نص دقيق موقّت بالثواني.",
      dropzoneCta: "اسحب الملف الصوتي هنا أو انقر للاختيار",
      dropzoneHint: "MP3، WAV، M4A وغيرها — حتى 25 ميغابايت",
      changeFile: "تغيير الملف",
      languageLabel: "اللغة",
      languageAr: "العربية",
      languageEn: "الإنجليزية",
      submit: "ابدأ التفريغ",
      submitting: "جارٍ الرفع...",
      noFile: "اختر ملفًا صوتيًا أولاً.",
      fileTooLarge: "حجم الملف أكبر من 25 ميغابايت.",
      resultTitle: "النتيجة",
      detectedLanguage: "اللغة المكتشَفة",
      duration: "المدة",
      billed: "المحتسب",
      copy: "نسخ النص",
      copied: "تم النسخ",
      segments: "المقاطع",
      failedPrefix: "فشل",
      unknownError: "خطأ غير معروف",
      balanceLabel: "رصيد الدقائق",
      minutesUnit: "د",
      secondsUnit: "ث",
      historyTitle: "السجل",
      historyEmpty: "لا توجد نصوص مفرّغة بعد.",
      noText: "(بدون نص)",
      status: {
        pending: "قيد الانتظار",
        processing: "جارٍ المعالجة",
        completed: "مكتمل",
        failed: "فشل",
      },
    },
    notifications: {
      title: "الإشعارات",
      markAllRead: "تحديد الكل كمقروء",
      markAsRead: "تحديد كمقروء",
      markAsUnread: "تحديد كغير مقروء",
      allCaughtUp: "أنت مطّلع على كل شيء",
      emptyDescription: "ستظهر الإشعارات الجديدة هنا.",
      genericUpdate: "تحديث جديد",
      untitledTranscript: "تفريغ صوتي بدون عنوان",
      refunded: "فشلت المهمة وتمت إعادة الدقائق المخصومة.",
      unavailable: "هذا العنصر لم يعد متاحًا.",
      loadError: "تعذر تحميل الإشعارات.",
      updateError: "تعذر تحديث الإشعار.",
      retry: "إعادة المحاولة",
      loadMore: "تحميل المزيد",
      loading: "جارٍ تحميل الإشعارات",
      unreadCount: (count) => `${count} إشعار غير مقروء`,
      download: "تنزيل",
      downloading: "جارٍ التنزيل…",
      downloadError: "تعذر تنزيل الفيديو.",
      copy: {
        transcription: {
          completed: "التفريغ الصوتي جاهز",
          failed: "فشل التفريغ الصوتي",
        },
        translation: {
          completed: "الترجمة جاهزة",
          failed: "فشلت الترجمة",
        },
        review: {
          completed: "مراجعة الذكاء الاصطناعي جاهزة",
          failed: "فشلت مراجعة الذكاء الاصطناعي",
        },
        video_export: {
          pending: "جارٍ إنشاء الفيديو…",
          completed: "تصدير الفيديو جاهز",
          failed: "فشل تصدير الفيديو",
        },
      },
    },
  },
};

const SECTION_MODULES = [shell, home, dialogs, captionSettings, editor];

export const translations = {
  en: Object.assign({}, baseTranslations.en, ...SECTION_MODULES.map((m) => m.en)),
  ar: Object.assign({}, baseTranslations.ar, ...SECTION_MODULES.map((m) => m.ar)),
};
